import * as Joi from 'joi';
import {ExamSchoolFormData} from '../../../models';
import {
  getDateOfCeqSchema,
  getJoiObject,
  getMasterCodeObjectDropdownSchema,
} from '../../../utils/JoiUtils';
import {ValidCeqTestDatesModel} from '../components/nonNationalExam/ExamSchoolingDetailsForm';

const alphanumericRegex = /^[0-9A-Z]*$/;
const alphaRegex = /^[A-Z]*$/;
const numericRegex = /^[0-9]*$/;

const invalidCentreReferenceNo = 'Invalid Centre Reference No';

// 1. For Centre Reference No. - char limit 10, first 5 must be alphanumeric, followed by space, last 4 characters must be numbers. (eg. GB599 0001)
const validateCentreReferenceNo = (value: string, helpers: Joi.CustomHelpers) => {
  const inputValue = value.toLocaleUpperCase();

  const alphanumericPart = inputValue.slice(0, 5);

  if (!alphanumericRegex.test(alphanumericPart)) {
    return helpers.error('string.pattern.start_part');
  }
  const spacePart = inputValue.charAt(5);
  if (spacePart !== ' ') {
    return helpers.error('string.pattern.space_part');
  }
  const nummericPart = inputValue.slice(6);
  if (!numericRegex.test(nummericPart)) {
    return helpers.error('string.pattern.numeric_part');
  }
  return value;
};

// Verification No. - textfield, mandatory, char limit 8, first character must be letter, remaining 7 characters must be numbers
const validateVerificationNo = (value: string, helpers: Joi.CustomHelpers) => {
  const inputValue = value.toLocaleUpperCase();

  const firstCharacter = inputValue.charAt(0);
  if (!alphaRegex.test(firstCharacter)) {
    return helpers.error('string.pattern.first_letter');
  }
  const nummericPart = inputValue.slice(1);
  if (!numericRegex.test(nummericPart)) {
    return helpers.error('string.pattern.numeric_part');
  }
  return value;
};

const minScoreMessage = (selectedAdmissionLevel: string): string => {
  let messages: string = '';
  switch (selectedAdmissionLevel) {
    case 'PRIMARY 2':
      messages = `Invalid Score. You need to obtain at least an overall score of 100 to register`;
      break;
    case 'PRIMARY 3':
      messages = `Invalid Score. You need to obtain at least an overall score of 120 to register`;
      break;
    case 'PRIMARY 4':
      messages = `Invalid Score. You need to obtain CEFR A2 to register`;
      break;
    case 'PRIMARY 5':
      messages = `Invalid Score. You need to obtain CEFR B1 to register`;
      break;

    default:
      break;
  }
  return messages;
};

const maxScoreMessage = (selectedAdmissionLevel: string): string => {
  let messages: string = '';
  switch (selectedAdmissionLevel) {
    case 'PRIMARY 2':
    case 'PRIMARY 3':
    case 'PRIMARY 4':
      messages = `Invalid Score. You are only allowed to enter scores obtained from A2 Key for School`;
      break;
    case 'PRIMARY 5':
      messages = `Invalid Score.  You are only allowed to enter scores obtained from B1 Preliminary for School`;
      break;

    default:
      break;
  }
  return messages;
};

const examSchooling = (
  validCeqTestDates: ValidCeqTestDatesModel | undefined,
  isFullForm: boolean,
  maxScore: number = 999,
  minScore: number = 0,
  selectedAdmissionLevel: string = '',
  minOverallScore: number = 100,
  isOpsAdmin = false
) =>
    Joi.object<ExamSchoolFormData>({
      motherTongueLanguageToBeTaken: getMasterCodeObjectDropdownSchema(
          false,
          'Mother Tongue Language to be taken'
      ),
      hasStudyingInSingapore: Joi.string().required().messages({
        'any.required': 'Please select your choice',
        'string.base': `Please select your choice`,
      }),
      hasSibling: Joi.string().required().messages({
        'any.required': 'Please select your choice',
        'string.base': `Please select your choice`,
      }),
      verificationNo: getJoiObject(
          Joi.string().length(8).required().custom(validateVerificationNo, 'Verification No').messages({
            'string.empty': 'Verification No is not allowed to be empty',
            'any.required': 'Verification No is required',
            'string.length': 'Verification No must be 8 characters',
            'string.pattern.first_letter': 'Invalid Verification No',
            'string.pattern.numeric_part': 'Invalid Verification No',
          }),
          !isFullForm
      ),
      centerReferenceNo: getJoiObject(
          Joi.string()
          .required()
          .length(10)
          .custom(validateCentreReferenceNo, 'Centre Reference No')
          .messages({
            'string.empty': 'Centre Reference No  is not allowed to be empty',
            'string.length': 'Centre Reference No must be 10 characters',
            'any.required': 'Centre Reference No is required',
            'string.pattern.start_part': invalidCentreReferenceNo,
            'string.pattern.space_part': invalidCentreReferenceNo,
            'string.pattern.numeric_part': invalidCentreReferenceNo,
          }),
          !isFullForm
      ),
      ceqType: getJoiObject(getMasterCodeObjectDropdownSchema(false, 'CEQ Type'), !isFullForm),
      countryCEQ: getJoiObject(
          getMasterCodeObjectDropdownSchema(false, 'Select the country where CEQ was taken'),
          !isFullForm
      ),
      dateOfCEQ: getJoiObject(getDateOfCeqSchema(validCeqTestDates), !isFullForm),
      listening: getJoiObject(
          Joi.number()
          .required()
          .max(maxScore)
          .min(minScore)
          .messages({
            'number.base': 'Listening must be a number',
            'number.max': maxScoreMessage(selectedAdmissionLevel),
            'number.min': minScoreMessage(selectedAdmissionLevel),
          }),
          !isFullForm
      ),
      overall: getJoiObject(
          Joi.number()
          .required()
          .max(999)
          .min(minOverallScore)
          .messages({
            'number.base': 'Overall must be a number',
            'number.max': maxScoreMessage(selectedAdmissionLevel),
            'number.min': minScoreMessage(selectedAdmissionLevel),
          }),
          !isFullForm
      ),
      reading: getJoiObject(
          Joi.number()
          .required()
          .max(maxScore)
          .min(minScore)
          .messages({
            'number.base': 'Reading must be a number',
            'number.max': maxScoreMessage(selectedAdmissionLevel),
            'number.min': minScoreMessage(selectedAdmissionLevel),
          }),
          !isFullForm
      ),

    speaking: getJoiObject(
      Joi.number()
        .required()
        .max(maxScore)
        .min(minScore)
        .messages({
          'number.base': 'Speaking must be a number',
          'number.max': maxScoreMessage(selectedAdmissionLevel),
          'number.min': minScoreMessage(selectedAdmissionLevel),
        }),
      !isFullForm
    ),
    writing: getJoiObject(
      Joi.number()
        .required()
        .max(maxScore)
        .min(minScore)
        .messages({
          'number.base': 'Writing must be a number',
          'number.max': maxScoreMessage(selectedAdmissionLevel),
          'number.min': minScoreMessage(selectedAdmissionLevel),
        }),
      !isFullForm
    ),
    selectedAdmissionLevel: getJoiObject(
      Joi.string().required().messages({
        'string.base': `Please select You are Now Seeking Admission to`,
      }),
      !isOpsAdmin
    ),
  });
export default examSchooling;
